import { LocalStorageCache } from '@auth0/auth0-react';
import {
  ActivityApi,
  ActivityInstanceApi,
  AnalysisInstanceApi,
  AnalyticsDashboardApi,
  AssertionApi,
  BusinessUnitApi,
  ChatApi,
  DailyBriefApi,
  DocumentApi,
  DocumentInstanceApi,
  AdminEvalApi,
  AdminEvalCaseApi,
  AdminEvalRunApi,
  FileApi,
  JobsApi,
  KnowledgeApi,
  FollowUpApi,
  GroupApi,
  HolidayApi,
  ManagedProjectApi,
  MilestoneApi,
  MilestoneInstanceApi,
  NotificationApi,
  OrganizationApi,
  OutputInstanceApi,
  PdfApi,
  RoleApi,
  ScheduledTasksApi,
  DocumentWebApi,
  AuditApi,
  HealthApi,
  Peoplev2Api,
} from 'app/data/sdk';
import axios, { AxiosError } from 'axios';
import { AdminApi } from '~/data/admin-api/admin.api';
import type {
  AnalyzeFileEntry,
  AnalyzeIntegrationFilesResponse,
  CreateIntegrationIngestionPlanInput,
  CreateIntegrationInput,
  IntegrationFile,
  IntegrationIngestionPlan,
  IntegrationIngestionRun,
  IntegrationListItem,
  IntegrationUploadUrlResponse,
  ReconciliationRun,
  StartIntegrationIngestionRunInput,
  StartReconciliationInput,
  UpdateIntegrationInput,
} from '~/data/endpoints/integrations';
import type { DataCatalogRow } from '~/data/endpoints/data-catalog';
import type {
  AuditLogPage,
  CreateGoldGroupBody,
  CreateGoldRbacUserBody,
  EntityColumnValuesResponse,
  EntityPolicy,
  EntityPolicyListResponse,
  EntityPolicyMutationResult,
  GoldGroup,
  GoldGroupListResponse,
  GoldRbacUser,
  GoldRbacUserListResponse,
  ListAuditLogParams,
  PutEntityPolicyBody,
  SyncAuth0MembersResponse,
  UpdateGoldGroupBody,
  UpdateGoldRbacUserBody,
} from '~/data/endpoints/admin-rbac';

const interceptorsApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

/**
 * Wire shape of a Context Wiki entry (`AgentLearning` row) returned by
 * `/agent-knowledge/learnings/*`.
 *
 * This mirrors the API view (`AgentLearningView` / `AgentLearningListItemView`)
 * after the Context Wiki migration. `VALIDATED_QUERY` was retired as a `type`/
 * `kind` value — the server's `@IsIn` guard rejects it on write, though legacy
 * rows may still carry it historically. The deprecated `kind` and `category`
 * fields are still emitted by the server for back-compat with legacy clients;
 * new code should consume `type` and `tags` instead.
 */
type AgentLearningWire = {
  id: string;
  type: 'FACT' | 'PREFERENCE' | 'PATTERN' | 'CONSTRAINT';
  status: 'ACTIVE' | 'PROPOSED' | 'ARCHIVED';
  /** @deprecated Mirror of `type`. */
  kind: 'PATTERN';
  domain: string;
  tags: string[];
  /** @deprecated Use `tags`. */
  category?: string;
  agentId?: string;
  title: string;
  content: string;
  usedCount: number;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
  scope: 'ORG' | 'PROJECT' | 'USER';
  projectId?: string;
  userId?: string;
};

type Auth0Token = {
  body?: {
    access_token?: string;
  };
};

interceptorsApi.interceptors.request.use(async (config) => {
  const cache = new LocalStorageCache();
  const keys = cache.allKeys();
  const authAudience = import.meta.env.VITE_AUTH_AUDIENCE || '';
  const tokenKey = keys.find((key) => key.includes(authAudience));

  if (!tokenKey) {
    return config;
  }

  const token = await cache.get(tokenKey);
  const accessToken = (token as Auth0Token).body?.access_token;

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

/**
 * Request path suffixes whose 401 responses must NOT escalate to a global
 * re-login (`auth:force-login`).
 *
 * A 401 from `GET /auth/access` means the caller holds a valid Auth0 token for
 * the active org but has no RBAC provisioning in that org yet — an
 * authorization/provisioning outcome, not a session/authentication failure.
 * Re-authenticating cannot mint the missing RBAC record, so escalating it to
 * `auth:force-login` produces an infinite Auth0 org-picker loop for a user who
 * is mid-provisioning (e.g. a freshly accepted second-org invite whose gold
 * `user` row does not exist). The 401 is surfaced to the calling query instead,
 * which handles the "no access" state without redirecting. See SZ-2782.
 */
const FORCE_LOGIN_EXEMPT_PATH_SUFFIXES = ['/auth/access'];

/**
 * A 401 only means "log in again" when the request actually carried a session.
 * During the Auth0 code exchange the cache is empty, so startup calls such as
 * `GET /organizations/current` go out unauthenticated. Treating that 401 as
 * `auth:force-login` aborts the exchange and sends a multi-org user back to
 * the organization picker. See SZ-3369.
 */
function requestHadAccessToken(error: AxiosError): boolean {
  const headers = error.config?.headers;
  if (!headers) {
    return false;
  }

  const authorization = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
  return typeof authorization === 'string' && authorization.length > 0;
}

/**
 * Whether a 401 for the given failed request should trigger `auth:force-login`.
 *
 * @param error - The rejected Axios error (its `config.url` identifies the route).
 * @returns `false` for unauthenticated requests and for RBAC access-probe endpoints that must not force a re-login.
 */
function shouldForceLoginOnUnauthorized(error: AxiosError): boolean {
  if (!requestHadAccessToken(error)) {
    return false;
  }

  const requestUrl = error.config?.url ?? '';
  return !FORCE_LOGIN_EXEMPT_PATH_SUFFIXES.some((suffix) => requestUrl.includes(suffix));
}

interceptorsApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error?.response?.status === 401 && shouldForceLoginOnUnauthorized(error)) {
      window.dispatchEvent(new CustomEvent('auth:force-login'));
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

export { interceptorsApi as apiAxios };

export const api = {
  activity: new ActivityApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  activityInstance: new ActivityInstanceApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  analysisInstance: new AnalysisInstanceApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  analyticsDashboard: new AnalyticsDashboardApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  assertion: new AssertionApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  businessUnit: new BusinessUnitApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  chat: new ChatApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  dailyBrief: new DailyBriefApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  document: new DocumentApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  documentInstance: new DocumentInstanceApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  file: new FileApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  followUp: new FollowUpApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  group: new GroupApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  holiday: new HolidayApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  managedProject: new ManagedProjectApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  milestone: new MilestoneApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  milestoneInstance: new MilestoneInstanceApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  notification: new NotificationApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  eval: new AdminEvalApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  evalCase: new AdminEvalCaseApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  evalRun: new AdminEvalRunApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  organization: new OrganizationApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  outputInstance: new OutputInstanceApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  pdf: new PdfApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  role: new RoleApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  scheduledTasks: new ScheduledTasksApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  documentWeb: new DocumentWebApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  jobs: new JobsApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  knowledge: new KnowledgeApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  agentConfig: {
    list: () =>
      interceptorsApi.get<Array<{ id: string; agentId: string; instructions: string; updatedAt: string }>>(
        '/organizations/current/agent-configs',
      ),
    get: (agentId: string) =>
      interceptorsApi.get<{ id: string; agentId: string; instructions: string; updatedAt: string }>(
        `/organizations/current/agent-configs/${agentId}`,
      ),
    upsert: (body: { agentId: string; instructions: string }) =>
      interceptorsApi.put<{ id: string; agentId: string; instructions: string; updatedAt: string }>(
        '/organizations/current/agent-configs',
        body,
      ),
    delete: (agentId: string) => interceptorsApi.delete(`/organizations/current/agent-configs/${agentId}`),
  },
  agentKnowledge: {
    list: (params: {
      q?: string;
      domain?: string;
      /** MECE classification filter. `VALIDATED_QUERY` was retired. */
      type?: 'FACT' | 'PREFERENCE' | 'PATTERN' | 'CONSTRAINT';
      /** Lifecycle filter; pass `ANY` to include archived/proposed rows. */
      status?: 'ACTIVE' | 'PROPOSED' | 'ARCHIVED' | 'ANY';
      /** Free-form tag filter (matches against the `tags` array). */
      tag?: string;
      /** @deprecated Use `type` instead. */
      kind?: 'PATTERN';
      /** @deprecated Use `tag` instead. */
      category?: string;
      scope?: 'ORG' | 'PROJECT' | 'USER' | 'ANY';
      projectId?: string;
      userId?: string;
      limit?: number;
      offset?: number;
    }) =>
      interceptorsApi.get<{
        items: Array<AgentLearningWire>;
        total: number;
      }>(`/agent-knowledge/learnings`, { params }),
    get: (id: string) => interceptorsApi.get<AgentLearningWire>(`/agent-knowledge/learnings/${id}`),
    create: (body: {
      title: string;
      content: string;
      /** @deprecated Optional legacy classifier; server defaults to `general`. */
      domain?: string;
      /** @deprecated Optional MECE classification; server defaults to `FACT`. */
      type?: 'FACT' | 'PREFERENCE' | 'PATTERN' | 'CONSTRAINT';
      tags?: string[];
      status?: 'ACTIVE' | 'PROPOSED' | 'ARCHIVED';
      /** @deprecated Use `type`. */
      kind?: 'PATTERN';
      /** @deprecated Use `tags`. */
      category?: string;
      agentId?: string;
      scope?: 'ORG' | 'PROJECT' | 'USER';
      projectId?: string;
      metadata?: Record<string, unknown>;
    }) => interceptorsApi.post<AgentLearningWire>(`/agent-knowledge/learnings`, body),
    update: (
      id: string,
      body: {
        title?: string;
        content?: string;
        domain?: string;
        type?: 'FACT' | 'PREFERENCE' | 'PATTERN' | 'CONSTRAINT';
        status?: 'ACTIVE' | 'PROPOSED' | 'ARCHIVED';
        tags?: string[];
        /** @deprecated Use `tags`. */
        category?: string | null;
      },
    ) => interceptorsApi.patch<AgentLearningWire>(`/agent-knowledge/learnings/${id}`, body),
    delete: (id: string) => interceptorsApi.delete<{ id: string }>(`/agent-knowledge/learnings/${id}`),
  },
  audit: new AuditApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  admin: new AdminApi(interceptorsApi),
  health: new HealthApi({ isJsonMime: () => true }, undefined, interceptorsApi),
  peoplev2: new Peoplev2Api({ isJsonMime: () => true }, undefined, interceptorsApi),
  integration: {
    list: (params?: Record<string, unknown>) =>
      interceptorsApi.get<{ items: IntegrationListItem[]; count: number }>('/integrations', {
        params: { limit: 200, offset: 0, ...params },
      }),
    get: (id: string) => interceptorsApi.get<IntegrationListItem>(`/integrations/${id}`),
    create: (input: CreateIntegrationInput) => interceptorsApi.post<IntegrationListItem>('/integrations', input),
    createWithFiles: (input: {
      name: string;
      description?: string;
      type: 'csv' | 'documents' | 'people';
      authType?: string;
      config?: Record<string, unknown>;
      files: Array<{
        fileName: string;
        contentType: string;
        fileSize?: number;
        metadata?: Record<string, unknown>;
      }>;
    }) =>
      interceptorsApi.post<{
        integration: IntegrationListItem;
        uploads: Array<{ fileId: string; fileName: string; uploadUrl: string; expiresAt: string }>;
      }>('/integrations/create-with-files', input),
    update: (id: string, input: UpdateIntegrationInput) =>
      interceptorsApi.patch<IntegrationListItem>(`/integrations/${id}`, input),
    delete: (id: string) => interceptorsApi.delete(`/integrations/${id}`),
    test: (id: string) => interceptorsApi.post<IntegrationListItem>(`/integrations/${id}/test`),
    getUploadUrl: (body: { fileName: string; contentType: string; integrationId?: string }) =>
      interceptorsApi.post<IntegrationUploadUrlResponse>('/integrations/upload-url', body),
    analyzeFiles: (body: { files: AnalyzeFileEntry[] }) =>
      interceptorsApi.post<AnalyzeIntegrationFilesResponse>('/integrations/analyze-files', body),
    /**
     * @deprecated Use {@link mintFiles} instead. The new endpoint creates
     * `IntegrationFile` rows AND mints signed GCS upload URLs in one round
     * trip with bucket/path stamped server-side, eliminating the trap
     * class where the FE had to ferry bucket/path between two BE calls
     * (cause of the 2026-05 FDE smoke regression). Kept for back-compat
     * with MCP / CLI clients.
     */
    addFiles: (
      id: string,
      body: {
        fileIds: string[];
        fileMetadata?: Array<{
          fileId: string;
          fileName: string;
          contentType: string;
          fileSize?: number;
          metadata?: Record<string, unknown>;
          /** Lowercase-hex MD5 of the file body (32 chars). Persisted on `IntegrationFile.contentMd5`. */
          contentMd5?: string;
          /** ISO date string from the upload wizard's free date picker. */
          periodDate?: string;
          /** Origin label for `periodDate`. Convention: `'user'` for wizard picks. */
          periodSource?: string;
          /**
           * Phase-4: bucket the file landed in (echoed back from
           * `IntegrationUploadUrlResponse`). Persisted on
           * `IntegrationFile.metadata.uploadBucket`.
           */
          gcsBucket?: 'raw' | 'nonConf' | 'chat';
          gcsPath?: string;
        }>;
      },
    ) => interceptorsApi.post<IntegrationListItem>(`/integrations/${id}/files`, body),
    /**
     * Single-round-trip "attach files to an existing integration".
     * Replaces the legacy `getUploadUrl` + `addFiles` two-step. The
     * server creates `File` + `IntegrationFile` rows AND mints the
     * signed GCS upload URL in one call, with `metadata.uploadBucket`
     * / `uploadPath` stamped server-side.
     *
     * Caller workflow:
     *   const { files } = (await api.integration.mintFiles(integrationId, { files: [...] })).data;
     *   for (const f of files) await fetch(f.uploadUrl, { method: 'PUT', body, headers });
     */
    mintFiles: (
      id: string,
      body: {
        files: Array<{
          fileName: string;
          contentType: string;
          fileSize?: number;
          /** Lowercase-hex MD5 (32 chars). Triggers the dedup pre-check on the server. */
          contentMd5?: string;
          /** ISO date string from the upload wizard's free date picker. */
          periodDate?: string;
          /** Origin label for `periodDate`. Convention: `'user'` for wizard picks. */
          periodSource?: string;
          /**
           * Free-form metadata blob merged onto `IntegrationFile.metadata`
           * AFTER the BE-stamped `uploadBucket` / `uploadPath`. Reserved
           * keys `uploadBucket` / `uploadPath` cannot be overridden — the
           * BE always wins.
           */
          metadata?: Record<string, unknown>;
        }>;
      },
    ) =>
      interceptorsApi.post<{
        files: Array<{
          fileId: string;
          fileName: string;
          uploadUrl: string;
          expiresAt: string;
          gcsBucket: 'raw' | 'nonConf' | 'chat';
          gcsPath: string;
        }>;
        integration: IntegrationListItem;
      }>(`/integrations/${id}/files/mint`, body),
    checkFileMd5: (id: string, md5: string) =>
      interceptorsApi.get<{
        found: boolean;
        file?: {
          id: string;
          fileId: string;
          fileName: string;
          createdAt: string;
          periodDate?: string;
          periodSource?: string;
        };
      }>(`/integrations/${id}/files/check-md5`, { params: { md5 } }),
    createIngestionPlan: (id: string, body: CreateIntegrationIngestionPlanInput) =>
      interceptorsApi.post<IntegrationIngestionPlan>(`/integrations/${id}/ingestion-plans`, body),
    getIngestionPlan: (id: string, planId: string) =>
      interceptorsApi.get<IntegrationIngestionPlan>(`/integrations/${id}/ingestion-plans/${planId}`),
    updateIngestionPlan: (id: string, planId: string, body: { planJson: Record<string, unknown>; note?: string }) =>
      interceptorsApi.patch<IntegrationIngestionPlan>(`/integrations/${id}/ingestion-plans/${planId}`, body),
    startIngestionRun: (id: string, body: StartIntegrationIngestionRunInput) =>
      interceptorsApi.post<IntegrationIngestionRun>(`/integrations/${id}/ingestion-runs`, body),
    listIngestionRuns: (id: string, params?: { limit?: number; offset?: number; status?: string }) =>
      interceptorsApi.get<{ items: IntegrationIngestionRun[]; count: number }>(`/integrations/${id}/ingestion-runs`, {
        params,
      }),
    getIngestionRun: (id: string, runId: string) =>
      interceptorsApi.get<IntegrationIngestionRun>(`/integrations/${id}/ingestion-runs/${runId}`),
    startReconciliation: (id: string, body: StartReconciliationInput) =>
      interceptorsApi.post<ReconciliationRun>(`/integrations/${id}/reconcile`, body),
    listReconciliations: (id: string) =>
      interceptorsApi.get<ReconciliationRun[]>(`/integrations/${id}/reconciliations`),
    getReconciliation: (id: string, runId: string) =>
      interceptorsApi.get<ReconciliationRun>(`/integrations/${id}/reconciliations/${runId}`),
    listFiles: (id: string, params?: { limit?: number; offset?: number }) =>
      interceptorsApi.get<{ items: IntegrationFile[]; count: number }>(`/integrations/${id}/files`, { params }),
    getFile: (integrationId: string, fileId: string, params?: { includeRawSensitive?: boolean }) =>
      interceptorsApi.get<
        IntegrationFile & {
          organizationId: string;
          userId: string;
          downloadUrl?: string;
          canViewRawSensitivePreview?: boolean;
          deIdPending?: boolean;
        }
      >(`/integrations/${integrationId}/files/${fileId}`, {
        params: params?.includeRawSensitive ? { includeRawSensitive: true } : {},
      }),
    updateFile: (integrationId: string, fileId: string, body: { metadata?: Record<string, unknown> }) =>
      interceptorsApi.patch<IntegrationFile>(`/integrations/${integrationId}/files/${fileId}`, body),
    deleteFile: (integrationId: string, fileId: string) =>
      interceptorsApi.delete(`/integrations/${integrationId}/files/${fileId}`),
    profileFile: (integrationId: string, fileId: string) =>
      interceptorsApi.post<{
        columns: string[];
        rowCount: number;
        formatType: string;
        columnProfiles: Array<{
          name: string;
          inferredType: string;
          nullRate: number;
          uniqueRate: number;
          totalCount: number;
          nullCount: number;
          uniqueCount: number;
          sampleValues: unknown[];
          patterns: string[];
        }>;
        sampleRows: string[][];
        metadata?: Record<string, unknown>;
      }>(`/integrations/${integrationId}/files/${fileId}/profile`),
    /**
     * Phase-4 §6 — silver-layer preview (post-cleanse-and-enrich Parquet)
     * for the latest completed run that produced silver for this file.
     * 404 with body `{ code: "NO_SILVER_YET" }` when no silver exists yet;
     * the caller is expected to render the empty-state.
     */
    getSilverPreview: (integrationId: string, fileId: string) =>
      interceptorsApi.get<{
        columns: string[];
        rowCount: number;
        formatType: string;
        columnProfiles: Array<{
          name: string;
          inferredType: string;
          nullRate: number;
          uniqueRate: number;
          totalCount: number;
          nullCount: number;
          uniqueCount: number;
          sampleValues: unknown[];
          patterns: string[];
        }>;
        sampleRows: string[][];
        metadata?: Record<string, unknown>;
        runId: string;
        silverGeneratedAt?: string;
        silverGcsUri?: string;
      }>(`/integrations/${integrationId}/files/${fileId}/silver-preview`),
    startOAuth: (id: string, body?: { redirectTo?: string }) =>
      interceptorsApi.post<{ authUrl: string }>(`/integrations/${id}/oauth/start`, body ?? {}),
    listFileVersions: (_integrationId: string, fileId: string) =>
      interceptorsApi.get(`/knowledge/files/${fileId}/versions`),
    restoreFileVersion: (_integrationId: string, fileId: string, body: { versionId: string }) =>
      interceptorsApi.post(`/knowledge/files/${fileId}/restore`, body),
    listFileComments: (integrationId: string, fileId: string) =>
      interceptorsApi.get(`/integrations/${integrationId}/files/${fileId}/comments`),
    createFileComment: (integrationId: string, fileId: string, body: { content: string }) =>
      interceptorsApi.post(`/integrations/${integrationId}/files/${fileId}/comments`, body),
    deleteFileComment: (integrationId: string, fileId: string, commentId: string) =>
      interceptorsApi.delete(`/integrations/${integrationId}/files/${fileId}/comments/${commentId}`),
    listMembers: (integrationId: string) => interceptorsApi.get(`/integrations/${integrationId}/members`),
    addMember: (integrationId: string, body: { userId: string; role: string }) =>
      interceptorsApi.post(`/integrations/${integrationId}/members`, body),
    updateMember: (integrationId: string, memberId: string, body: { role: string }) =>
      interceptorsApi.patch(`/integrations/${integrationId}/members/${memberId}`, body),
    removeMember: (integrationId: string, memberId: string) =>
      interceptorsApi.delete(`/integrations/${integrationId}/members/${memberId}`),
  },
  /**
   * Data Catalog (Phase 7 / M3).
   *
   * `Cache-Control: no-cache` request headers force the browser to
   * revalidate every time, even if the upstream returned an ETag
   * before. The catalog mutates whenever an FDE edits a description
   * or a new run lands, and a 304 round-trip would silently keep an
   * empty array on screen. React Query's own in-memory cache still
   * gives the user back-button snappiness via `staleTime`.
   */
  dataCatalog: {
    list: (params?: { integrationId?: string }) =>
      interceptorsApi.get<{ items: DataCatalogRow[] }>('/data-catalog', {
        params,
        headers: { 'Cache-Control': 'no-cache' },
      }),
    search: (q: string) =>
      interceptorsApi.get<{ items: DataCatalogRow[]; query: string }>('/data-catalog/search', {
        params: { q },
        headers: { 'Cache-Control': 'no-cache' },
      }),
  },
  /**
   * Gold-RBAC Epic 4 — org-admin `/admin/rbac/*` surface (sz-api Epic 3).
   * Path params that may contain `|` (Auth0 subs) / `:` (SurrealDB record ids)
   * are `encodeURIComponent`-escaped so they survive the URL path segment.
   */
  adminRbac: {
    listEntityPolicies: () => interceptorsApi.get<EntityPolicyListResponse>('/admin/rbac/entity-policies'),
    getEntityPolicy: (entityName: string) =>
      interceptorsApi.get<EntityPolicy>(`/admin/rbac/entity-policies/${encodeURIComponent(entityName)}`),
    listEntityColumnValues: (entityName: string, column: string, options?: { limit?: number; q?: string }) =>
      interceptorsApi.get<EntityColumnValuesResponse>(
        `/admin/rbac/entity-policies/${encodeURIComponent(entityName)}/columns/${encodeURIComponent(column)}/values`,
        {
          params: {
            ...(options?.limit !== undefined ? { limit: options.limit } : {}),
            ...(options?.q ? { q: options.q } : {}),
          },
        },
      ),
    putEntityPolicy: (entityName: string, body: PutEntityPolicyBody) =>
      interceptorsApi.put<EntityPolicyMutationResult>(
        `/admin/rbac/entity-policies/${encodeURIComponent(entityName)}`,
        body,
      ),
    listUsers: (includeOffboarded?: boolean) =>
      interceptorsApi.get<GoldRbacUserListResponse>('/admin/rbac/users', {
        params: includeOffboarded ? { includeOffboarded: true } : undefined,
      }),
    getUser: (auth0Sub: string) =>
      interceptorsApi.get<GoldRbacUser>(`/admin/rbac/users/${encodeURIComponent(auth0Sub)}`),
    createUser: (body: CreateGoldRbacUserBody) => interceptorsApi.post<GoldRbacUser>('/admin/rbac/users', body),
    syncAuth0Members: () => interceptorsApi.post<SyncAuth0MembersResponse>('/admin/rbac/users/sync-auth0-members'),
    updateUser: (auth0Sub: string, body: UpdateGoldRbacUserBody) =>
      interceptorsApi.patch<GoldRbacUser>(`/admin/rbac/users/${encodeURIComponent(auth0Sub)}`, body),
    deleteUser: (auth0Sub: string) =>
      interceptorsApi.delete<undefined>(`/admin/rbac/users/${encodeURIComponent(auth0Sub)}`),
    hardDeleteUser: (auth0Sub: string) =>
      interceptorsApi.delete<undefined>(`/admin/rbac/users/${encodeURIComponent(auth0Sub)}/permanent`),
    reactivateUser: (auth0Sub: string) =>
      interceptorsApi.post<GoldRbacUser>(`/admin/rbac/users/${encodeURIComponent(auth0Sub)}/reactivate`),
    listGroups: () => interceptorsApi.get<GoldGroupListResponse>('/admin/rbac/groups'),
    getGroup: (groupId: string) => interceptorsApi.get<GoldGroup>(`/admin/rbac/groups/${encodeURIComponent(groupId)}`),
    createGroup: (body: CreateGoldGroupBody) => interceptorsApi.post<GoldGroup>('/admin/rbac/groups', body),
    updateGroup: (groupId: string, body: UpdateGoldGroupBody) =>
      interceptorsApi.patch<GoldGroup>(`/admin/rbac/groups/${encodeURIComponent(groupId)}`, body),
    deleteGroup: (groupId: string) =>
      interceptorsApi.delete<undefined>(`/admin/rbac/groups/${encodeURIComponent(groupId)}`),
    listAuditLog: (params?: ListAuditLogParams) =>
      interceptorsApi.get<AuditLogPage>('/admin/rbac/audit-log', { params }),
  },
};
